import axios from "axios"
import { ARBITRUM_RPC_URL, DOTS_PROJECT_CONTRACT_ADDRESS, DOTS_PROJECT_ID } from "config"

const ERC721_TRANSFER_TOPIC =
  "0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef"
const ZERO_ADDRESS_TOPIC = `0x${"0".repeat(64)}`
const DOTS_TOKEN_ID_BASE = Number(DOTS_PROJECT_ID) * 1000000

interface JsonRpcResponse<T> {
  result?: T
  error?: {
    code: number
    message: string
  }
}

interface TransactionLog {
  address: string
  topics: string[]
}

interface TransactionReceipt {
  logs: TransactionLog[]
}

export interface CompositeSourceDot {
  tokenId: string
  invocation?: number
}

const isSameAddress = (addressA?: string, addressB?: string) =>
  Boolean(addressA && addressB && addressA.toLowerCase() === addressB.toLowerCase())

const parseTokenIdTopic = (topic?: string) => {
  if (!topic) return undefined

  try {
    return BigInt(topic).toString()
  } catch {
    return undefined
  }
}

export const getDotsInvocation = (tokenId: string) => {
  const numericTokenId = Number(tokenId)

  if (!Number.isSafeInteger(numericTokenId)) return undefined
  if (numericTokenId >= DOTS_TOKEN_ID_BASE) return numericTokenId - DOTS_TOKEN_ID_BASE

  return numericTokenId
}

export const extractCompositeSourceDots = (receipt: TransactionReceipt): CompositeSourceDot[] => {
  const seenTokenIds = new Set<string>()

  return receipt.logs.reduce<CompositeSourceDot[]>((sourceDots, log) => {
    if (!isSameAddress(log.address, DOTS_PROJECT_CONTRACT_ADDRESS)) return sourceDots
    if (log.topics[0] !== ERC721_TRANSFER_TOPIC) return sourceDots
    if (log.topics[1] === ZERO_ADDRESS_TOPIC || log.topics[2] === ZERO_ADDRESS_TOPIC) return sourceDots

    const tokenId = parseTokenIdTopic(log.topics[3])
    if (!tokenId || seenTokenIds.has(tokenId)) return sourceDots

    seenTokenIds.add(tokenId)
    sourceDots.push({
      tokenId,
      invocation: getDotsInvocation(tokenId),
    })

    return sourceDots
  }, [])
}

export const fetchCompositeSourceDots = async (mintTransactionHash: string) => {
  const response = await axios.post<JsonRpcResponse<TransactionReceipt | null>>(ARBITRUM_RPC_URL, {
    jsonrpc: "2.0",
    id: 1,
    method: "eth_getTransactionReceipt",
    params: [mintTransactionHash],
  })

  if (response.data.error) {
    throw new Error(response.data.error.message)
  }

  if (!response.data.result) return []

  return extractCompositeSourceDots(response.data.result)
}
